import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Plus as AddIcon, Settings as SettingsIcon } from 'lucide-react';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import { Project, Task } from '../../types';
import KanbanColumn from './KanbanColumn';
import KanbanConfig from './KanbanConfig';

interface KanbanBoardProps {
  project: Project;
  onTaskUpdate?: (task: Task) => void;
  onTaskCreate?: (task: Partial<Task>) => void;
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({
  project,
  onTaskUpdate,
  onTaskCreate,
}) => {
  const [tasks, setTasks] = useState<Task[]>(project.tasks || []);
  const [columns, setColumns] = useState<string[]>([
    'Not Started',
    'In Progress',
    'Completed',
  ]);
  const [loading, setLoading] = useState(false);
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [selectedColumn, setSelectedColumn] = useState('Not Started');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTasks(project.tasks || []);
  }, [project.tasks]);

  const handleDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId } = result;

    // If dropped outside a valid droppable area
    if (!destination) {
      return;
    }

    // If dropped in same position
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const taskId = parseInt(draggableId);
    const updatedTask = {
      ...tasks.find((t) => t.id === taskId)!,
      status: destination.droppableId as any,
      position: destination.index,
    };

    // Optimistic update
    setTasks(
      tasks.map((t) => (t.id === taskId ? updatedTask : t))
    );

    // Update in parent
    if (onTaskUpdate) {
      onTaskUpdate(updatedTask);
    }
  };

  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) {
      setError('Task title cannot be empty');
      return;
    }

    const newTask: Partial<Task> = {
      title: newTaskTitle,
      status: selectedColumn as any,
      priority: 'Medium',
      completed: false,
    };

    if (onTaskCreate) {
      onTaskCreate(newTask);
    }

    setNewTaskTitle('');
    setNewTaskOpen(false);
    setError(null);
  };

  const handleColumnsUpdate = (newColumns: string[]) => {
    setColumns(newColumns);
    setConfigOpen(false);
  };

  const getTasksByStatus = (status: string): Task[] => {
    return tasks.filter((t) => t.status === status).sort((a, b) => (a.position || 0) - (b.position || 0));
  };

  return (
    <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <h2>Kanban Board</h2>
        <Button
          startIcon={<SettingsIcon size={18} />}
          onClick={() => setConfigOpen(true)}
          variant="outlined"
          size="small"
        >
          Configure Columns
        </Button>
      </Stack>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            pb: 2,
          }}
        >
          {columns.map((status) => (
            <KanbanColumn
              key={status}
              status={status}
              tasks={getTasksByStatus(status)}
              projectId={project.id}
            />
          ))}

          {/* Add Column Button */}
          <Box
            sx={{
              minWidth: 300,
              p: 2,
              bgcolor: '#f5f5f5',
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Button
              fullWidth
              startIcon={<AddIcon size={18} />}
              onClick={() => setNewTaskOpen(true)}
              variant="text"
              sx={{ justifyContent: 'flex-start' }}
            >
              Add Task
            </Button>
          </Box>
        </Box>
      </DragDropContext>

      {/* Add Task Dialog */}
      <Dialog open={newTaskOpen} onClose={() => setNewTaskOpen(false)}>
        <DialogTitle>Create New Task</DialogTitle>
        <DialogContent sx={{ minWidth: 400 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            autoFocus
            margin="dense"
            label="Task Title"
            type="text"
            fullWidth
            value={newTaskTitle}
            onChange={(e) => setNewTaskTitle(e.target.value)}
            variant="outlined"
            sx={{ mt: 2 }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddTask();
              }
            }}
          />
          <TextField
            margin="dense"
            label="Status"
            select
            fullWidth
            value={selectedColumn}
            onChange={(e) => setSelectedColumn(e.target.value)}
            variant="outlined"
            sx={{ mt: 2 }}
            SelectProps={{
              native: true,
            }}
          >
            {columns.map((col) => (
              <option key={col} value={col}>
                {col}
              </option>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewTaskOpen(false)}>Cancel</Button>
          <Button onClick={handleAddTask} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kanban Config Dialog */}
      <KanbanConfig
        open={configOpen}
        columns={columns}
        onClose={() => setConfigOpen(false)}
        onSave={handleColumnsUpdate}
      />
    </Box>
  );
};

export default KanbanBoard;
